import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC } from 'react';
import { OrderProperty } from 'types/order';
import { useTranslation } from 'react-i18next';
import { SplitPayeePayer } from 'views/orders/new-charges/components/split-payee/split-payee';
import { useTabOwnershipHooks } from './tab-ownership-hooks';
import TableComponent from 'components/Table';
import { formatMoney } from 'utils/helpers';
import { formatPercent } from 'views/orders/loans/utils';

const useStyles = makeStyles((theme: any) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "flex",
    gap: "1rem",
    [theme.breakpoints.down('md')]: { flexDirection: 'column' },
  },
  currentOwnersContainer: {
    flex: 1,
    minWidth: 0
  },
  newOwnersContainer: {
    flex: 1,
    minWidth: 0
  },
  splitButton: {
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down('md')]: { minWidth: 44, minHeight: 44 },
    '@media (hover: none) and (pointer: coarse)': { minWidth: 44, minHeight: 44 },
  },
  titleContainer: {
    display: "flex",
    alignItems: "center"
  },
  primarycolorfonts: {
    fontWeight: 500,
    color: theme.palette.tab.offselected,
  },
  placeholderText: {
    color: theme.palette.tab.offselected,
    fontWeight: 500,
    textAlign: 'center',
    marginTop: theme.spacing(5),
  },
}));

type TabOwnershipProps = {
  selectedProperty: OrderProperty,
};

export const TabOwnership: FC<TabOwnershipProps> = ({
  selectedProperty,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    modalState,
    handleCloseModal,
    purchasePriceEntry,
    handleShowSplitModal,
    ownerships,
    proprietors
  } = useTabOwnershipHooks({ selectedProperty });

  return (
    <Box className={classes.container}>
      <Box className={classes.currentOwnersContainer}>
        <Box className={classes.titleContainer}>
          <Typography variant="h6" className={classes.primarycolorfonts}>
            {t('orders:current-owners')}
          </Typography>
          <IconButton
            size="small"
            className={classes.splitButton}
            aria-label={t('orders:split-current-owners')}
            onClick={() => handleShowSplitModal("Proprietor")}
          >
            <FontAwesomeIcon icon="users" />
          </IconButton>
        </Box>

        <TableComponent
          disabledBorderLeft
          cellAlignment="left"
          columns={{
            name: {
              header: { title: t('accounting:name') },
              renderer: ({ row }) => <>{row.name}</>,
            },
            kind: {
              header: { title: "Percent" },
              renderer: ({ row }) => <>{formatPercent(row.percent)}</>,
            },
            amount: {
              header: { title: t('accounting:amount') },
              renderer: ({ row }) =>
                <>
                  {formatMoney(row.amount)}
                </>,
            },
          }}
          dataSet={proprietors}
        />
      </Box>

      <Box className={classes.newOwnersContainer}>
        <Box className={classes.titleContainer}>
          <Typography variant="h6" className={classes.primarycolorfonts}>
            {t('orders:new-owners')}
          </Typography>
          <IconButton
            size="small"
            className={classes.splitButton}
            aria-label={t('orders:split-new-owners')}
            onClick={() => handleShowSplitModal("Ownership")}
          >
            <FontAwesomeIcon icon="users" />
          </IconButton>
        </Box>

        <TableComponent
          disabledBorderLeft
          cellAlignment="left"
          columns={{
            name: {
              header: { title: t('accounting:name') },
              renderer: ({ row }) => <>{row.name}</>,
            },
            kind: {
              header: { title: "Percent" },
              renderer: ({ row }) => <>{formatPercent(row.percent)}</>,
            },
            amount: {
              header: { title: t('accounting:amount') },
              renderer: ({ row }) =>
                <>
                  {formatMoney(row.amount)}
                </>,
            },
          }}
          dataSet={ownerships}
        />
      </Box>

      {modalState && (
        <SplitPayeePayer
          open
          kind={modalState.kind}
          accountingEntry={purchasePriceEntry}
          onClose={handleCloseModal}
          onSuccess={() => handleCloseModal()}
        />
      )}
    </Box>
  );
};
